import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { eventService, registrationService, minorService } from '../services/database';
import { Event as SupabaseEvent, EventDistance } from '../lib/supabase';
import { getCurrentUser } from '../utils/auth';
import { 
  faChevronLeft, 
  faShare, 
  faCalendarDays, 
  faLocationDot, 
  faGaugeHigh, 
  faUsers, 
  faClock,
  faRoute,
  faMoneyBill,
  faShield,
  faInfoCircle,
  faHouse,
  faUserGroup,
  faUser,
  faCheck,
  faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import RegisterModal from '../components/RegisterModal';

// Interface for transformed distances used in the component
interface TransformedDistance {
  id: string;
  name: string;
  length: string;
  price: string;
  description: string;
  ageRestriction: string;
  difficulty: string;
}

// Helper function to calculate age
const calculateAge = (dateOfBirth: string) => {
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age;
};

const EventDetails: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [isRegistering, setIsRegistering] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registrationStatus, setRegistrationStatus] = useState<{participantId: string, participantType: string} | null>(null);
  const [event, setEvent] = useState<SupabaseEvent | null>(null);
  const [distances, setDistances] = useState<TransformedDistance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [minors, setMinors] = useState<any[]>([]);

  // Load event data from database
  useEffect(() => {
    if (eventId) {
      loadEvent(eventId);
      loadEventDistances(eventId);
    }
    loadMinors();
  }, [eventId]);

  const loadMinors = async () => {
    try {
      // Get the current logged-in user
      const currentUser = getCurrentUser();
      if (!currentUser) {
        console.log('No user logged in, no minors to load');
        setMinors([]);
        return;
      }

      // Load real minors for the logged-in user
      const minorsData = await minorService.getUserMinors(currentUser.id);
      
      // Transform to match the expected format for RegisterModal
      const transformedMinors = minorsData.map(minor => ({
        id: minor.id,
        name: minor.name,
        age: calculateAge(minor.date_of_birth),
        gender: minor.gender,
        category: minor.rider_category,
        medical_conditions: minor.medical_conditions,
        allergies: minor.allergies,
        emergency_contact_name: minor.emergency_contact_name,
        emergency_contact_phone: minor.emergency_contact_phone,
        emergency_contact_relationship: minor.emergency_contact_relationship
      }));
      
      setMinors(transformedMinors);
      
      console.log('Loaded real minors for user:', {
        userId: currentUser.id,
        minorsCount: transformedMinors.length,
        minors: transformedMinors.map(m => ({ name: m.name, age: m.age }))
      });
      
    } catch (error) {
      console.error('Error loading minors:', error);
      setMinors([]);
    }
  };

  const loadEvent = async (id: string) => {
    try {
      setLoading(true);
      const eventData = await eventService.getEventById(id);
      setEvent(eventData);
      setError(null);
    } catch (err) {
      console.error('Error loading event:', err);
      setError('Failed to load event details.');
    } finally {
      setLoading(false);
    }
  };

  const loadEventDistances = async (id: string) => {
    try {
      const distancesData = await eventService.getEventDistances(id);
      console.log('Loaded distances from database:', distancesData);
      
      if (distancesData && distancesData.length > 0) {
        // Transform to match the expected format for RegisterModal
        const transformedDistances = distancesData.map(distance => ({
          id: distance.id,
          name: distance.name,
          length: distance.length,
          price: `R${distance.price}`,
          description: distance.description,
          ageRestriction: distance.age_restriction,
          difficulty: distance.difficulty
        }));
        console.log('Transformed distances:', transformedDistances);
        setDistances(transformedDistances);
      } else {
        console.log('No distances found in database for event:', id);
        // Set empty array instead of dummy data
        setDistances([]);
      }
    } catch (error) {
      console.error('Error loading event distances:', error);
      // Set empty array on error instead of dummy data
      setDistances([]);
    }
  };

  // Remove duplicate - function is defined above with state

  // Handle loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen px-4">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
        <p className="text-gray-600">Loading event details...</p>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen px-4">
        <h2 className="text-xl font-semibold mb-4">Error Loading Event</h2>
        <p className="text-gray-600 mb-6">{error}</p>
        <div className="flex gap-4">
          <button 
            onClick={() => eventId && loadEvent(eventId)}
            className="bg-primary text-white px-6 py-2 rounded-lg"
          >
            Retry
          </button>
          <button 
            onClick={() => navigate('/events')}
            className="bg-gray-500 text-white px-6 py-2 rounded-lg"
          >
            Back to Events
          </button>
        </div>
      </div>
    );
  }

  // Handle if event is not found
  if (!event) {
    return (
      <div className="flex flex-col items-center justify-center h-screen px-4">
        <h2 className="text-xl font-semibold mb-4">Event Not Found</h2>
        <p className="text-gray-600 mb-6">The event you're looking for doesn't exist or has been removed.</p>
        <button 
          onClick={() => navigate('/events')}
          className="bg-primary text-white px-6 py-2 rounded-lg"
        >
          Back to Events
        </button>
      </div>
    );
  }

  // Format dates for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  // Get event image - use database image_url or fallback to title-based selection
  const getEventImage = (event: SupabaseEvent) => {
    // Use database image_url if available
    if (event.image_url) {
      return event.image_url;
    }
    
    // Fallback to title-based selection
    const title = event.title.toLowerCase();
    if (title.includes('kids') || title.includes('family')) {
      return 'https://storage.googleapis.com/uxpilot-auth.appspot.com/1e47f714b4-6bd43a89fc178a2bf2cb.png';
    }
    if (title.includes('championship') || title.includes('race')) {
      return 'https://storage.googleapis.com/uxpilot-auth.appspot.com/d82ea563d8-78875ce6151593f0a740.png';
    }
    if (title.includes('workshop') || title.includes('training')) {
      return 'https://storage.googleapis.com/uxpilot-auth.appspot.com/85a91caa1e-62302dc765829cb4a791.png';
    }
    return 'https://storage.googleapis.com/uxpilot-auth.appspot.com/0143432743-43182f97ff610c1ad975.png';
  };

  // Get event badge
  const getEventBadge = (event: SupabaseEvent) => {
    const now = new Date();
    const registrationDeadline = new Date(event.registration_deadline);
    const eventDate = new Date(event.date);
    
    if (eventDate < now) {
      return { text: 'Past Event', color: 'bg-gray-500' };
    }
    
    if (registrationDeadline < now) {
      return { text: 'Registration Closed', color: 'bg-red-500' };
    }
    
    const percentFull = (event.current_participants / event.max_participants) * 100;
    
    if (percentFull >= 90) {
      return { text: 'Almost Full', color: 'bg-red-500' };
    }
    
    if (percentFull >= 70) {
      return { text: 'Filling Up', color: 'bg-yellow-500' };
    }
    
    if (event.description && (event.description.toLowerCase().includes('family') || event.description.toLowerCase().includes('kids'))) {
      return { text: 'Family Friendly', color: 'bg-secondary' };
    }
    
    return { text: 'Registration Open', color: 'bg-primary' };
  };

  const eventBadge = getEventBadge(event);

  const handleRegisterClick = () => {
    setShowRegisterModal(true);
  };

  const handleRegisterModalClose = () => {
    setShowRegisterModal(false);
  };

  const handleRegisterParticipants = (participants: {id: string, type: 'self' | 'minor', distanceId: string}[], totalPrice: number) => {
    setShowRegisterModal(false);
    setIsRegistering(true);
    
    // Registration is now handled by the RegisterModal component
    // This callback is called after successful registration
    setTimeout(() => {
      // Create a summary of who was registered
      const selfRegistered = participants.find(p => p.type === 'self');
      const minorsRegistered = participants.filter(p => p.type === 'minor');
      
      // Format registration details with distances
      const formatParticipantWithDistance = (participant: {id: string, type: 'self' | 'minor', distanceId: string}) => {
        const distance = distances.find(d => d.id === participant.distanceId);
        return distance ? `${distance.name} (${distance.length})` : 'Unknown distance';
      };
      
      let registrationMessage = '';
      
      if (selfRegistered && minorsRegistered.length > 0) {
        // Self and minors registered
        const selfDistance = formatParticipantWithDistance(selfRegistered);
        
        const minorDetails = minorsRegistered.map(p => {
          const minor = minors.find(m => m.id === p.id);
          const distance = formatParticipantWithDistance(p);
          return minor ? `${minor.name} - ${distance}` : 'Unknown minor';
        }).join('\n• ');
        
        registrationMessage = `Registration Confirmed for ${event.title}!\n\n`;
        registrationMessage += `You have been registered for the ${selfDistance} distance.\n\n`;
        registrationMessage += `Your ${minorsRegistered.length > 1 ? 'minors have' : 'minor has'} been registered for:\n• ${minorDetails}`;
      } else if (selfRegistered) {
        // Only self registered
        const selfDistance = formatParticipantWithDistance(selfRegistered);
        registrationMessage = `Registration Confirmed for ${event.title}!\n\nYou have been registered for the ${selfDistance} distance.`;
      } else if (minorsRegistered.length > 0) {
        // Only minors registered
        const minorDetails = minorsRegistered.map(p => {
          const minor = minors.find(m => m.id === p.id);
          const distance = formatParticipantWithDistance(p);
          return minor ? `${minor.name} - ${distance}` : 'Unknown minor';
        }).join('\n• ');
        
        registrationMessage = `Registration Confirmed for ${event.title}!\n\n`;
        registrationMessage += `Your ${minorsRegistered.length > 1 ? 'minors have' : 'minor has'} been registered for:\n• ${minorDetails}`;
      }
      
      registrationMessage += `\n\nTotal payment: R${totalPrice.toFixed(2)}`;
      
      alert(registrationMessage);
      setIsRegistering(false);
    }, 1500);
  };

  const handleShare = () => {
    // In a real app, this would open a share dialog
    alert('Share functionality would be implemented here');
  };

  return (
    <div className="bg-gray-50 font-sans text-dark">
      {/* Header */}
      <header className="app-header">
        <div className="flex items-center justify-between">
          <Link to="/events" className="app-header-back">
            <FontAwesomeIcon icon={faArrowLeft} className="text-lg" />
            </Link>
          <h1 className="app-header-title">Event Details</h1>
          <div className="w-6"></div> {/* Empty div for spacing */}
        </div>
      </header>

      {/* Main Content */}
      <main className="content-container pt-16 pb-28">
        {/* Event Image */}
        <div className="relative h-56 w-full">
          <img 
            src={getEventImage(event)} 
            alt={event.title} 
            className="w-full h-full object-cover" 
          />
          <div className={`absolute top-4 right-4 ${eventBadge.color} text-white text-xs font-semibold px-2 py-1 rounded`}>
            {eventBadge.text}
            </div>
        </div>

        {/* Event Title and Registration */}
        <div className="bg-white p-4 shadow-sm">
          <h2 className="text-xl font-bold">{event.title}</h2>
          <div className="flex justify-between items-center mt-4">
            <span className="text-sm font-medium">Registration Deadline: {formatDate(event.registration_deadline)}</span>
            <button 
              className={`px-4 py-2 rounded-lg text-sm font-medium ${isRegistering ? 'bg-gray-400' : 'bg-primary'} text-white`}
              onClick={handleRegisterClick}
              disabled={isRegistering}
            >
              {isRegistering ? 'Registering...' : 'Register Now'}
            </button>
          </div>
        </div>

        {/* Event Details Section */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-4">
          <h3 className="text-lg font-semibold mb-3">Event Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="mb-2"><span className="font-medium">Date:</span> {formatDate(event.date)}</p>
              <p className="mb-2"><span className="font-medium">Time:</span> {formatTime(event.date)}</p>
              <p className="mb-2"><span className="font-medium">Location:</span> {event.location}</p>
              <p className="mb-2"><span className="font-medium">Max Participants:</span> {event.max_participants}</p>
            </div>
            <div>
              <p className="mb-2"><span className="font-medium">Description:</span> {event.description}</p>
                              <p className="mb-2"><span className="font-medium">Entry Fee:</span> {event.entry_fee_from || 'TBD'}</p>
              <p className="mb-2"><span className="font-medium">Registration Deadline:</span> {formatDate(event.registration_deadline)}</p>
              <p className="mb-2"><span className="font-medium">Registered:</span> {event.current_participants}/{event.max_participants}</p>
            </div>
          </div>
        </div>
        
        {/* Distance Options Section */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-4">
            <h3 className="text-lg font-semibold mb-3">Distance Options</h3>
            <div className="grid grid-cols-1 gap-3">
            {distances.length > 0 ? (
              distances.map((distance) => (
                <div key={distance.id} className="border rounded-md p-3 hover:bg-gray-50">
                  <div className="flex justify-between items-center mb-1">
                    <h4 className="font-semibold text-primary">{distance.name} ({distance.length})</h4>
                    <span className="font-semibold">{distance.price}</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-1">{distance.description}</p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                      Age: {distance.ageRestriction}
                    </span>
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                      Difficulty: {distance.difficulty}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-4">
                <p className="text-gray-500">Distance not available</p>
              </div>
            )}
            </div>
          </div>
        
        <div className="bg-white p-4 shadow-sm mt-4">
          <div className="space-y-4">
            <div className="flex items-start">
              <FontAwesomeIcon icon={faUsers} className="text-gray-500 mt-1 mr-3 w-5" />
              <div>
                <p className="font-medium">Participants</p>
                <p className="text-gray-600 text-sm">{event.current_participants}/{event.max_participants} Registered</p>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                  <div 
                    className="bg-primary h-2 rounded-full" 
                    style={{ width: `${(event.current_participants / event.max_participants) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
            
            <div className="flex items-start">
              <FontAwesomeIcon icon={faMoneyBill} className="text-gray-500 mt-1 mr-3 w-5" />
              <div>
                <p className="font-medium">Entry Fee</p>
                <p className="text-gray-600 text-sm">{event.entry_fee_from || 'TBD'} (varies by distance)</p>
              </div>
            </div>
          </div>
        </div>

        {/* Event Description */}
        <div className="bg-white mt-3 p-4 shadow-sm">
          <h3 className="font-semibold text-lg mb-3">About This Event</h3>
          <p className="text-gray-600 text-sm">{event.description}</p>
        </div>

        {/* Requirements */}
        <div className="bg-white mt-3 p-4 shadow-sm">
          <h3 className="font-semibold text-lg mb-3">Requirements</h3>
          <div className="flex items-start">
            <FontAwesomeIcon icon={faShield} className="text-gray-500 mt-1 mr-3 w-5" />
            <p className="text-gray-600 text-sm">{event.requirements || 'Helmet required, mountain bike in good condition, water bottle, and basic repair kit.'}</p>
          </div>
        </div>

        {/* Amenities */}
        {event.amenities && event.amenities.length > 0 && (
          <div className="bg-white mt-3 p-4 shadow-sm">
            <h3 className="font-semibold text-lg mb-3">What's Included</h3>
            <div className="grid grid-cols-2 gap-2">
              {event.amenities.map((amenity, index) => (
                <div key={index} className="flex items-center text-sm text-gray-600">
                  <FontAwesomeIcon icon={faCheck} className="text-green-500 mr-2 w-4" />
                  {amenity}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Organizer Info */}
        <div className="bg-white mt-3 p-4 shadow-sm mb-3">
          <h3 className="font-semibold text-lg mb-3">Organizer</h3>
          <div className="flex items-start">
            <FontAwesomeIcon icon={faInfoCircle} className="text-gray-500 mt-1 mr-3 w-5" />
            <div>
              <p className="font-medium">{event.organizer || 'GOMTB Events'}</p>
              <a href="#contact" className="text-primary text-sm">Contact organizer</a>
            </div>
          </div>
        </div>
      </main>

      {/* Bottom Navigation */}
      <footer className="app-bottom-nav">
        <div className="flex justify-around items-center px-2 py-3">
          <Link to="/dashboard" className="app-bottom-nav-link py-1">
            <FontAwesomeIcon icon={faHouse} className="text-xl" />
            <span className="text-xs mt-1">Home</span>
          </Link>
          <Link to="/events" className="app-bottom-nav-link-active py-1">
            <FontAwesomeIcon icon={faCalendarDays} className="text-xl" />
            <span className="text-xs mt-1">Events</span>
          </Link>
          <Link to="/team" className="app-bottom-nav-link py-1">
            <FontAwesomeIcon icon={faUserGroup} className="text-xl" />
            <span className="text-xs mt-1">My Team</span>
          </Link>
          <Link to="/profile" className="app-bottom-nav-link py-1">
            <FontAwesomeIcon icon={faUser} className="text-xl" />
            <span className="text-xs mt-1">Profile</span>
          </Link>
        </div>
      </footer>

      {/* Registration Modal */}
      <RegisterModal 
        isOpen={showRegisterModal}
        onClose={handleRegisterModalClose}
        eventId={event.id}
        eventTitle={event.title}
        eventPrice={(() => {
          if (distances.length > 0) {
            const prices = distances.map(d => parseFloat(d.price.replace(/[^0-9.]/g, '')));
            const minPrice = Math.min(...prices);
            const maxPrice = Math.max(...prices);
            return minPrice === maxPrice 
              ? `R${minPrice.toFixed(0)}` 
              : `From R${minPrice.toFixed(0)}`;
          }
          return event.entry_fee_from || "TBD";
        })()}
        distances={distances}
        minors={minors}
        onRegister={handleRegisterParticipants}
      />
    </div>
  );
};

export default EventDetails;
